import React from 'react';
import { Box, FormControl, FormHelperText, Grid, Card, CardContent, Typography, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

/**
 * TemplateSelector - Seletor visual de templates com preview
 * Mostra miniaturas de cada template permitindo seleção via checkbox visual
 * Converte URLs relativas de preview para URLs completas baseado no ambiente
 */
export const TemplateSelector = ({ templates = [], value, onChange, error }) => {
  // Resolver URL relativa do Static para URL completa (ambiente-aware)
  const getFullPreviewUrl = (relativeUrl) => {
    if (!relativeUrl) return '';
    
    // Se já é URL absoluta, retornar como está
    if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
      return relativeUrl;
    }
    
    // Static CDN - ambiente-aware
    const STATIC_CDN_DEV = 'https://static.dev.agendario.com.br';
    const STATIC_CDN_PROD = 'https://static.agendario.com.br';
    const isDev = import.meta.env.MODE === 'development' || window.location.hostname.includes('localhost');
    const CDN_BASE = isDev ? STATIC_CDN_DEV : STATIC_CDN_PROD;
    
    return CDN_BASE + relativeUrl;
  };

  if (!templates || templates.length === 0) {
    return (
      <FormControl fullWidth error={!!error}>
        <Typography variant="body2" color="textSecondary">
          Nenhum template disponível
        </Typography>
      </FormControl>
    );
  }

  const handleSelect = (templateId) => {
    onChange(templateId);
  };

  return (
    <FormControl fullWidth error={!!error}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Escolha um Template
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Selecione o layout que melhor se adequa ao seu negócio
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {templates.map((template) => {
          const isSelected = value === template.id;
          const Icon = isSelected ? CheckCircleIcon : RadioButtonUncheckedIcon;
          const imageAlt = template.description ? `${template.name} - ${template.description}` : template.name;

          return (
            <Grid item xs={12} sm={6} md={3} key={template.id}>
              <Tooltip 
                title={template.description || template.name}
                placement="top"
              >
                <Card
                  onClick={() => handleSelect(template.id)}
                  sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    border: (theme) => isSelected ? `3px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.grey[300]}`,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    backgroundColor: (theme) => isSelected ? theme.palette.action.selected : theme.palette.background.paper,
                    '&:hover': {
                      borderColor: (theme) => theme.palette.primary.main,
                      boxShadow: (theme) => theme.shadows[3],
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {/* Checkbox no canto superior direito */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 10,
                      color: (theme) => isSelected ? theme.palette.primary.main : theme.palette.text.disabled,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, fontWeight: 'bold' }} />
                  </Box>

                  {/* Preview da imagem */}
                  {template.previewImageUrl && (
                    <Box
                      sx={(theme) => ({
                        width: '100%',
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.grey[100],
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      })}
                    >
                      <Box
                        component="img"
                        src={getFullPreviewUrl(template.previewImageUrl)}
                        alt={imageAlt}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                  )}

                  {/* Nome */}
                  <CardContent sx={{ py: 1.25 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '1rem',
                        mb: 0,
                        color: (theme) => isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                      }}
                    >
                      {template.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {error && (
        <FormHelperText sx={{ mt: 2, color: '#d32f2f' }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default TemplateSelector;
